import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeMount } from 'vue';
import { toDataModel, setState, getChildModel, updateModel } from 'ah-common-lib/src/form/helpers';
import {
  AboutUsReference,
  ClientType,
  UserCreationRequest,
  BaseRegistrationModel,
  aboutUsReferenceOptions,
  AddressHistoryItem,
  CurrentAddressHistoryItem,
  EntityAddressHistory,
  checkAddressHistoryValidity,
  Address,
} from 'ah-api-gateways';
import { SecurePasswordChecker } from 'ah-common-lib/src/form/components';
import { FormEvent, FormValidation, CompoundValidation } from 'ah-common-lib/src/form/interfaces';
import RegistrationAddressForm from '../common/RegistrationAddressForm.vue';
import OnboardingPreviousAddressesForms from '../common/OnboardingPreviousAddressesForms.vue';
import { accountAccessFM } from '@/app/helpers/registration/forms';
import { cloneDeep, sortBy } from 'lodash';
import { submitUser } from '@/app/helpers/registration/requests';
import { useFEFeatureFlag } from 'ah-common-stores';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useToast } from 'ah-common-lib/src/toast';
import { useSettingsStore } from '@/app/store/settingsModule';


export default /*#__PURE__*/_defineComponent({
  __name: 'PersonalInformationForm',
  props: {
    model: null
  },
  emits: {
  'update:model': (_value: Partial<BaseRegistrationModel>) => true,
  'update:historyAddresses': (_value: Partial<EntityAddressHistory>) => true,
  proceed: () => true,
  failure: () => true,
},
  setup(__props: any, { emit }) {

const props = __props as {
  model: Partial<BaseRegistrationModel>;
};





const toast = useToast();

const settingsStore = useSettingsStore();

const { manager: requestManager } = useRequestManager();

const dobInOnboardingFeatureActive = useFEFeatureFlag('dobInOnboardingFeatureActive');

const accountAccessForm = ref(
  accountAccessFM({
    isCompanyApplicant: false,
    useDOB: dobInOnboardingFeatureActive.value,
  })
);
const accountAccessValidation = ref<FormValidation | null>(null);
const currentAddress = ref<CurrentAddressHistoryItem>({ residingFrom: '' } as CurrentAddressHistoryItem);
const addressValidation = ref<FormValidation | null>(null);
const addressHistoryValidation = ref<CompoundValidation | null>(null);
const previousAddresses = ref<AddressHistoryItem[]>([]);
const showErrorMessage = ref(false);

const isUKResident = computed(() => currentAddress.value?.countryCode === 'GB');

const isAddressesHistoryValid = () => {
  if (!isUKResident.value) {
    return true;
  }
  const addressHistoryValidity = checkAddressHistoryValidity({
    currentAddress: currentAddress.value || {},
    previousAddresses: previousAddresses.value,
  });

  return addressHistoryValidity.age && addressHistoryValidity.gaps;
};

const calculatedModel = computed((): Partial<BaseRegistrationModel> => {
  const { referenceDescription, referenceSelection, ...applicant } = toDataModel(accountAccessForm.value);
  return {
    ...props.model,
    reference: referenceSelection === AboutUsReference.OTHER ? referenceDescription : referenceSelection,
    applicant: {
      ...props.model.applicant,
      ...applicant,
    },
    address: { ...props.model.address!, ...currentAddress.value },
  };
});

const historyAddresses = computed(
  (): Partial<EntityAddressHistory> => ({
    currentAddress: currentAddress.value,
    previousAddresses: previousAddresses.value,
  })
);

const userCreationRequest = computed((): UserCreationRequest | null => {
  if (!currentAddress.value) return null;
  let { ...address } = currentAddress.value;

  return {
    applicant: calculatedModel.value.applicant!,
    registrationData: {
      ...(dobInOnboardingFeatureActive.value ? { birthDate: calculatedModel.value.applicant!.birthDate } : {}),
      questionnaireAnswer: calculatedModel.value.reference?.length ? calculatedModel.value.reference : undefined,
      address,
      nationality: calculatedModel.value.applicant?.nationality!,
      referralId: calculatedModel.value.referralId,
    },
  };
});

const isFormInvalid = computed(
  () =>
    !!accountAccessValidation.value?.$invalid ||
    !!addressValidation.value?.$invalid ||
    !!addressHistoryValidation.value?.$invalid
);

const emitUpdate = () => {
  emit('update:model', calculatedModel.value);
};

const onAddressChange = (value: Partial<Address>) => {
  currentAddress.value = {
    ...props.model.address,
    ...(value as CurrentAddressHistoryItem),
    countryCode: props.model.address?.countryCode!,
  };
  delete (currentAddress.value as any).countryName;
  emitUpdate();
};

const onFormEvent = (formEvent: FormEvent) => {
  if (formEvent.event === 'form-field-set-value') {
    setState(accountAccessForm.value, 'errors', [], true);

    if (formEvent.model.$name === 'referenceSelection') {
      checkReferenceDescriptionDisplay();
      accountAccessForm.value.referenceDescription = '';
      if (accountAccessValidation.value?.reference) {
        accountAccessValidation.value.reference.$reset();
      }
    }
    emitUpdate();
  }
};

const checkReferenceDescriptionDisplay = () => {
  const field = getChildModel(accountAccessForm.value, 'referenceDescription')!;
  setState(field, 'hidden', accountAccessForm.value.referenceSelection !== AboutUsReference.OTHER);
  setState(field, 'required', accountAccessForm.value.referenceSelection === AboutUsReference.OTHER);
};

const submit = () => {
  if (!isAddressesHistoryValid()) {
    toast.error(
      'Please review address information. UK residents must provide addresses spanning back 3 years, with no gaps.'
    );
    showErrorMessage.value = true;
    return;
  }
  showErrorMessage.value = false;

  requestManager
    .currentOrNew(
      'submitPersonalDetails',
      submitUser(props.model.clientType ?? ClientType.INDIVIDUAL, userCreationRequest.value!, accountAccessForm.value)
    )
    .subscribe(
      () => {
        emit('update:historyAddresses', historyAddresses.value);
        emit('proceed');
      },
      () => {
        emit('failure');
      }
    );
};

onBeforeMount(() => {
  const nationalityField = getChildModel(accountAccessForm.value, 'nationality');
  if (nationalityField) {
    setState(
      nationalityField,
      'options',
      sortBy(
        settingsStore.allCountries.map((c) => ({
          value: c.cc,
          label: c.nationality,
        })),
        (i) => i.label
      )
    );
  }
});

watch(
  [() => props.model, dobInOnboardingFeatureActive],
  () => {
    accountAccessForm.value = accountAccessFM({
      isCompanyApplicant: false,
      useDOB: dobInOnboardingFeatureActive.value,
    });
    updateModel(accountAccessForm.value, { ...props.model?.applicant });
    currentAddress.value = cloneDeep(props.model?.address ?? ({ residingFrom: '' } as any));

    if (typeof props.model?.reference === 'string') {
      if (
        aboutUsReferenceOptions
          .map((obj) => obj.value)
          .filter((v) => v !== AboutUsReference.OTHER)
          .includes(props.model.reference)
      ) {
        accountAccessForm.value.referenceSelection = props.model.reference;
      } else {
        accountAccessForm.value.referenceSelection = AboutUsReference.OTHER;
        accountAccessForm.value.referenceDescription = props.model.reference;
      }
      checkReferenceDescriptionDisplay();
    }

    const nationalityField = getChildModel(accountAccessForm.value, 'nationality');
    if (nationalityField) {
      setState(
        nationalityField,
        'options',
        sortBy(
          settingsStore.allCountries.map((c) => ({
            value: c.cc,
            label: c.nationality,
          })),
          (i) => i.label
        )
      );
    }
  },
  { immediate: true }
);

return { __sfc: true,props, emit, toast, settingsStore, requestManager, dobInOnboardingFeatureActive, accountAccessForm, accountAccessValidation, currentAddress, addressValidation, addressHistoryValidation, previousAddresses, showErrorMessage, isUKResident, isAddressesHistoryValid, calculatedModel, historyAddresses, userCreationRequest, isFormInvalid, emitUpdate, onAddressChange, onFormEvent, checkReferenceDescriptionDisplay, submit, SecurePasswordChecker, RegistrationAddressForm, OnboardingPreviousAddressesForms }
}

})